import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Calendar, Settings, LogOut, Menu, X, Film } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import logo from '../../assets/logo.png';
import ThemeToggle from '../../components/ui/ThemeToggle';
import { useConsoleTheme } from '../../lib/theme';

// /media layout — peer to AdminLayout, scoped to Media-role users.
//
// Two nav items in slice 2:
//   /media           — dashboard (own calendar + upcoming events)
//   /media/settings  — working hours, buffer, blocks, Google Calendar OAuth
//
// Future slices may add /media/events/:id detail view and /media/google.
// Sidebar pattern intentionally simpler than admin's — Media users have a
// small surface and don't need section dividers.

const NAV = [
  { to: '/media', label: 'Calendar', icon: Calendar, end: true },
  { to: '/media/settings', label: 'Settings', icon: Settings, end: false },
];

export default function MediaLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  useConsoleTheme(); // marks <html> with the console theme while mounted

  const onLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="portal-skin min-h-screen relative">
      <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 grain-bg opacity-[0.13]" />
      </div>

      <div className="relative z-10 flex">
        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-gray-300"
        >
          <Menu className="w-5 h-5" />
        </button>

        {/* Sidebar */}
        <aside
          data-media-sidebar
          className={`fixed inset-y-0 left-0 z-40 w-60 bg-black/40 backdrop-blur-xl border-r border-white/[0.04]
            transform transition-transform duration-200 md:translate-x-0
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between px-5 py-5 border-b border-white/[0.04]">
              <div className="flex items-center gap-2.5">
                <img src={logo} alt="NexSav" data-light-invert className="h-7 w-auto" />
                <div className="text-[10px] uppercase tracking-wider text-[#B0FC38] flex items-center gap-1">
                  <Film className="w-3 h-3" /> Media
                </div>
              </div>
              {/* -my-1: the 32px switch must not make this 28px (logo) row
                  taller, or the nav below shifts (dark mode stays as it was). */}
              <div className="flex items-center gap-1.5 -my-1">
                <ThemeToggle />
                <button
                  type="button"
                  onClick={() => setMobileOpen(false)}
                  className="md:hidden p-1 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <nav className="flex-1 px-2.5 py-4 space-y-0.5">
              {NAV.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] transition-colors ${
                        isActive
                          ? 'bg-gradient-to-r from-[#B0FC38]/[0.12] to-transparent text-white border border-[#B0FC38]/20'
                          : 'text-gray-400 hover:text-white hover:bg-white/[0.03]'
                      }`
                    }
                  >
                    <Icon className="w-4 h-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>

            <div className="px-3 py-3 border-t border-white/[0.04]">
              <div className="px-2 mb-2 text-[11px] text-gray-500 truncate">
                {user?.name || user?.email}
              </div>
              <button
                type="button"
                onClick={onLogout}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] text-gray-400 hover:text-white hover:bg-white/[0.03]"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </button>
            </div>
          </div>
        </aside>

        {mobileOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/60 z-30"
            onClick={() => setMobileOpen(false)}
          />
        )}

        <main className="flex-1 md:ml-60 min-h-screen px-4 md:px-8 pt-16 pb-6 md:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
